import type { Modifier } from "@popperjs/core"

const MENU_VIEWPORT_PAD = 16

const getViewportHeightPx = (): number => {
  if (typeof window === "undefined") {
    return 0
  }

  return window.visualViewport?.height ?? window.innerHeight
}

const computeMenuMaxHeightPx = (rect: DOMRect, placement: string): number => {
  const pad = MENU_VIEWPORT_PAD
  const vh = getViewportHeightPx()
  if (placement.startsWith("bottom")) {
    return Math.max(pad * 2, vh - rect.top - pad)
  }
  if (placement.startsWith("top")) {
    return Math.max(pad * 2, rect.bottom - pad)
  }

  return Math.max(pad * 2, vh - pad * 2)
}

type PendingRafChain = {
  outer?: number
  inner?: number
}

const pendingRafByMenu = new WeakMap<HTMLElement, PendingRafChain>()

const cancelPendingRafForMenu = (menuEl: HTMLElement) => {
  const pending = pendingRafByMenu.get(menuEl)
  if (!pending) {
    return
  }
  if (pending.outer != null) {
    cancelAnimationFrame(pending.outer)
  }
  if (pending.inner != null) {
    cancelAnimationFrame(pending.inner)
  }
  pendingRafByMenu.delete(menuEl)
}

/**
 * Double rAF lets layout settle after Popper writes; coalescing avoids stacking
 * many pending callbacks when the modifier runs in quick succession (scroll/resize).
 */
const scheduleCoalescedDoubleRaf = (menuEl: HTMLElement, apply: () => void) => {
  cancelPendingRafForMenu(menuEl)
  const pending: PendingRafChain = {}
  pending.outer = requestAnimationFrame(() => {
    pending.inner = requestAnimationFrame(() => {
      pendingRafByMenu.delete(menuEl)
      apply()
    })
  })
  pendingRafByMenu.set(menuEl, pending)
}

/**
 * Caps the menu list height to the space available in the viewport for the
 * current placement. A plain max-height (e.g. 100dvh) is not enough because
 * the panel is positioned below/above the trigger and can still extend past
 * the screen.
 */
const menuViewportMaxHeightModifier: Partial<Modifier<"menuViewportMaxHeight", Record<string, unknown>>> = {
  name: "menuViewportMaxHeight",
  enabled: true,
  phase: "afterWrite",
  fn({ state }) {
    if (typeof window === "undefined") {
      return
    }
    const { elements: { popper }, placement } = state
    const menuEl = popper.querySelector<HTMLElement>("[role=\"menu\"]")
    if (!menuEl) {
      return
    }
    const apply = () => {
      const rect = menuEl.getBoundingClientRect()
      const maxPx = computeMenuMaxHeightPx(rect, placement)
      menuEl.style.maxHeight = `${maxPx}px`
      menuEl.style.overflowY = "auto"
    }
    scheduleCoalescedDoubleRaf(menuEl, apply)
  },
}

export default menuViewportMaxHeightModifier
